import { useState } from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../DashboardContext';
import type { TopWalletData } from '../DashboardContext';
import { DashboardInfoTip } from './DashboardInfoTip';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';
import { useChartSurface } from '../dashboardChrome';
import WalletClientModal from '../../wallet/WalletClientModal';
import type { ClientWalletRow } from '../../wallet/walletTypes';
import { SYSTEM_WALLET_TAGS } from '../../wallet/walletTypes';

const formatShort = (value: number) => {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return String(value);
};

const truncateName = (name: string, maxLen = 14) =>
  name.length > maxLen ? `${name.substring(0, maxLen)}…` : name;

const WalletOverview = () => {
  const { topWallets, walletByTag, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();
  const chrome = useChartSurface();
  const [selected, setSelected] = useState<ClientWalletRow | null>(null);

  const formatTooltip = (value: number) =>
    `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)} ${currency}`;

  const tagLabel = (tag: string) => {
    const key = tag.trim().toLowerCase();
    if ((SYSTEM_WALLET_TAGS as readonly string[]).includes(key)) {
      return t(`common:wallet.tags.${key}`);
    }
    return tag;
  };

  const openWallet = (row: TopWalletData) => {
    setSelected({
      client_id: row.clientId,
      client: row.name,
      balance: row.balance,
      is_active: row.isActive,
    });
  };

  if (sectionLoading.wallet) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {[1, 2].map((i) => (
          <DashboardWidget key={i} chartHeight={300} sx={{ flex: 1 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
          </DashboardWidget>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <DashboardWidget
          sx={{ flex: 1 }}
          chartHeight={300}
          header={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('common:dashboard.wallet.topWallets')}
              </Typography>
              <DashboardInfoTip helpKey="wallet.topWallets" />
              <Chip label={topWallets.length} size="small" color="success" variant="outlined" />
            </Box>
          }
        >
          {topWallets.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
              <Typography>{t('common:dashboard.noData')}</Typography>
            </Box>
          ) : (
            <DashboardSizedChart height={300}>
              <BarChart data={topWallets} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.grid} horizontal={false} />
                <XAxis type="number" tickFormatter={formatShort} fontSize={11} stroke={chrome.axis} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={100}
                  fontSize={11}
                  stroke={chrome.axis}
                  tickFormatter={(value) => truncateName(String(value))}
                />
                <Tooltip formatter={(value: number) => [formatTooltip(value), t('common:dashboard.wallet.balance')]} />
                <Bar
                  dataKey="balance"
                  radius={[0, 4, 4, 0]}
                  barSize={20}
                  fill="#2e7d32"
                  cursor="pointer"
                  onClick={(_, index) => {
                    const row = topWallets[index];
                    if (row) openWallet(row);
                  }}
                >
                  {topWallets.map((row) => (
                    <Cell key={row.clientId} fill={row.isActive ? '#2e7d32' : '#9e9e9e'} />
                  ))}
                </Bar>
              </BarChart>
            </DashboardSizedChart>
          )}
        </DashboardWidget>

        <DashboardWidget
          sx={{ flex: 1 }}
          chartHeight={300}
          header={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('common:dashboard.wallet.byTag')}
              </Typography>
              <DashboardInfoTip helpKey="wallet.byTag" />
              <Chip label={walletByTag.length} size="small" color="info" variant="outlined" />
            </Box>
          }
        >
          {walletByTag.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
              <Typography>{t('common:dashboard.noData')}</Typography>
            </Box>
          ) : (
            <DashboardSizedChart height={300}>
              <BarChart
                data={walletByTag.map((row) => ({ ...row, label: tagLabel(row.tag) }))}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.grid} horizontal={false} />
                <XAxis type="number" tickFormatter={formatShort} fontSize={11} stroke={chrome.axis} />
                <YAxis type="category" dataKey="label" width={110} fontSize={11} stroke={chrome.axis} />
                <Tooltip formatter={(value: number) => [formatTooltip(value), t('common:dashboard.wallet.net')]} />
                <Bar dataKey="amount" radius={[0, 4, 4, 0]} barSize={20}>
                  {walletByTag.map((row) => (
                    <Cell key={row.tag} fill={row.amount >= 0 ? '#2e7d32' : '#d32f2f'} />
                  ))}
                </Bar>
              </BarChart>
            </DashboardSizedChart>
          )}
        </DashboardWidget>
      </Box>

      <WalletClientModal
        isOpen={!!selected}
        wallet={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

export default WalletOverview;
